import TYPES from '@/types';

// Application
import {
  SearchAllianzAccountReportQuery,
} from '@/modules/my-investment/allianz/allianz-account-report/application/queries';
import GetCustomerInvestorGoalBalanceQuery
  from '@/modules/flagship/customer-investor-goal-balance/application/query/get-customer-investor-goal-balance';
import GetSearchByCustomerQuery
  from '@/modules/flagship/investor-goal/search-by-customer/application/queries/get-search-by-customer-query';
import {
  SearchAllianzRecurringContributionsQuery,
} from '@/modules/my-investment/allianz/recurring-contributions/application/queries';
import CreateLinkWealthV2Command from '@/modules/flagship/investor-goal/link-sowos-wealth/application/commands/create-link-wealth-v2-command';

// Domain
import {
  LinkGoalsWealthStateManager,
} from '@/modules/flagship/investor-goal/link-sowos-wealth/domain/state/link-goals-wealth-state-manager';
import {
  StateManager as ContributionsStateManager,
} from '@/modules/my-investment/allianz/recurring-contributions/domain/state/state-manager';
import {
  ContributionGoalForLinkDto,
} from '@/modules/flagship/investor-goal/link-sowos-wealth/domain/dtos/contribution-goal-for-link-dto';
import { SearchByCustomerDto } from '@/modules/flagship/investor-goal/search-by-customer/domain/dtos/search-by-customer-dto';
import {
  LinkInvestorGoalsDto,
} from '@/modules/flagship/investor-goal/link-sowos-wealth/domain/dtos/link-investor-goals-dto';
import {
  CustomerInvestorGoalBalanceQueryEntity,
} from '@/modules/flagship/customer-investor-goal-balance/domain/entities/customer-investor-goal-balance-query-entity';
import Inject from '@/modules/shared/domain/di/inject';
import Translator from '@/modules/shared/domain/i18n/translator';
import { Values } from '@/modules/shared/domain/i18n/types';
import { MessageNotifier } from '@/modules/shared/domain/notifiers/message_notifier';

export default class FlagshipLinkGoalsWizardLinkWealthViewModel {
  @Inject(TYPES.SEARCH_ALLIANZ_ACCOUNT_REPORT_QUERY)
  private readonly search_allianz_account_report_query!: SearchAllianzAccountReportQuery;

  @Inject(TYPES.GET_CUSTOMER_INVESTOR_GOAL_BALANCE_QUERY)
  private readonly get_customer_investor_goal_balance_query!: GetCustomerInvestorGoalBalanceQuery;

  @Inject(TYPES.GET_SEARCH_BY_CUSTOMER_QUERY)
  private readonly get_search_by_customer_query!: GetSearchByCustomerQuery;

  @Inject(TYPES.GET_ALLIANZ_RECURRING_CONTRIBUTIONS_QUERY)
  private readonly get_allianz_recurring_contributions_query!:
    SearchAllianzRecurringContributionsQuery;

  @Inject(TYPES.CREATE_LINK_WEALTH_V2_COMMAND)
  private readonly create_link_wealth_v2_command!: CreateLinkWealthV2Command;

  @Inject(TYPES.LINK_GOALS_WEALTH_STATE_MANAGER)
  readonly link_goals_wealth_state_manager!: LinkGoalsWealthStateManager;

  @Inject(TYPES.ALLIANZ_RECURRING_CONTRIBUTIONS_STATE_MANAGER)
  private readonly contributions_state_manager!:
    ContributionsStateManager;

  @Inject(TYPES.I18N)
  readonly translator!: Translator;

  @Inject(TYPES.NOTIFIER)
  private readonly message_notifier!: MessageNotifier;

  readonly i18n_namespace = 'components.flagship.flagship-link-goals';

  private readonly customer_id = sessionStorage.getItem('user_id');

  link_goals_wealth_state = this.link_goals_wealth_state_manager.state;

  contributions_state = this.contributions_state_manager.state;

  private search_by_customer_dto: SearchByCustomerDto = {
    reload: true,
    associated_product_id: '',
    is_active: true,
  };

  private customer_investor_goal_balance: CustomerInvestorGoalBalanceQueryEntity = {
    investment_product_id: '',
    reload: true,
  };

  private STEPS = [
    'StepChooseGoalsWealth',
    'StepDistributeBalanceWealth',
    'StepConfigureContributionsWealth',
    'StepSchedulePaymentDateWealth',
  ];

  current_step = 0;

  current_component = this.STEPS[this.current_step];

  has_active_goals = false;

  translate = (message: string, values?: Values) => this.translator.translate(`${this.i18n_namespace}.${message}`, values);

  menu_steps = this.translate('wealth.steps_link_wealth_with_balance');

  initialize = async (associated_product_id: string) => {
    if (associated_product_id) {
      this.link_goals_wealth_state.associated_product_id = associated_product_id;
      this.customer_investor_goal_balance.investment_product_id = associated_product_id;
      this.search_by_customer_dto.associated_product_id = associated_product_id;
      await this.loadActiveGoals();
      this.setCorrectMenuSteps();
      await this.loadRecurrentContributionInformation();
    }
  }

  loadActiveGoals = async () => {
    try {
      // eslint-disable-next-line max-len
      const active_goals = await this.get_search_by_customer_query.execute(this.search_by_customer_dto);
      if (active_goals.length) {
        await this.loadCustomerInvestorGoalBalance();
      } else {
        await this.loadAllianzBalance();
      }
    } catch {
      this.message_notifier.showErrorNotification(this.translate('wealth.errors.load_active_goals'));
    }
  }

  loadCustomerInvestorGoalBalance = async () => {
    try {
      const { unassigned_balance } = await this.get_customer_investor_goal_balance_query
        .execute(this.customer_investor_goal_balance);
      this.link_goals_wealth_state.unassigned_balance = Math.floor(unassigned_balance);
    } catch (error) {
      if (JSON.parse(error).status_code !== 404) {
        this.message_notifier.showErrorNotification(this.translate('wealth.errors.load_customer_investor_goal_balance'));
      }
    }
  }

  loadAllianzBalance = async () => {
    try {
      const allianz_account_report_list = await this.search_allianz_account_report_query.execute(
        this.customer_id,
      );
      if (allianz_account_report_list.length) {
        allianz_account_report_list.forEach((position) => {
          if (position.found_code === 'SWSRFP') {
            this.link_goals_wealth_state.unassigned_balance = position.final_balance_mxn || 0;
          }
        });
      }
    } catch {
      this.message_notifier.showErrorNotification(this.translate('wealth.errors.get_allianz_balance'));
    }
  }

  loadRecurrentContributionInformation = async (reload = false) => {
    await this.get_allianz_recurring_contributions_query.execute(reload);
  }

  setCorrectMenuSteps = () => {
    this.STEPS.length = 0;
    this.STEPS.push('StepChooseGoalsWealth');
    if (this.link_goals_wealth_state.unassigned_balance >= 1) {
      this.STEPS.push('StepDistributeBalanceWealth');
      this.menu_steps = this.translate('wealth.steps_link_wealth_with_balance');
    } else {
      this.menu_steps = this.translate('wealth.steps_link_wealth_without_balance');
    }
    if (this.contributions_state.item.total_contribution < 1000000) {
      this.STEPS = this.STEPS.concat([
        'StepConfigureContributionsWealth',
        'StepConfigureSecondContributionWealth',
        'StepSchedulePaymentDateWealth',
      ]);
    }
  }

  nextStep = () => {
    if (this.current_step < this.STEPS.length) {
      this.current_step += 1;
      this.current_component = this.STEPS[this.current_step];
    }
  }

  prevStep = () => {
    if (this.current_step > 0) {
      this.current_step -= 1;
      this.current_component = this.STEPS[this.current_step];
    }
  }

  endProcess= () => {
    this.current_step = 0;
    this.current_component = this.STEPS[this.current_step];
  }

  createPayloadContribution = (contribution: ContributionGoalForLinkDto) => ({
    id: contribution.id,
    amount: contribution.amount.toString(),
    collection_day: contribution.collection_day,
    distributions: contribution.distributions.map(
      (distribution) => ({
        ...distribution,
        recurrent_assigned_amount: distribution.recurrent_assigned_amount.toString(),
      }),
    ),
  })

  createPayloadToLinkGoalsWithWealth = () => {
    const payload: LinkInvestorGoalsDto = {
      contributions: [],
      investor_goals: [],
    };
    this.link_goals_wealth_state.selected_goals.forEach((selected_goal) => {
      payload.investor_goals.push({
        investor_goal_id: selected_goal.investor_goal_id,
        active_goal_amount: selected_goal.active_goal_amount.toString(),
        recurrent_assigned_amount: selected_goal.recurrent_assigned_amount.toString(),
      });
    });
    if (!this.link_goals_wealth_state.define_contribution_later) {
      if (this.link_goals_wealth_state.first_goal_contribution.id) {
        const first_contribution = this.createPayloadContribution(
          this.link_goals_wealth_state.first_goal_contribution,
        );
        payload.contributions.push(first_contribution);
      }
      if (this.link_goals_wealth_state.second_goal_contribution.id) {
        const second_contribution = this.createPayloadContribution(
          this.link_goals_wealth_state.second_goal_contribution,
        );
        payload.contributions.push(second_contribution);
      }
    }
    return payload;
  }

  linkGoals = async () => {
    try {
      this.link_goals_wealth_state.is_loading = true;
      const payload = this.createPayloadToLinkGoalsWithWealth();
      await this.create_link_wealth_v2_command.execute(payload);
      const linked_goals_event = new CustomEvent('wealth.linked.goal', {
        detail: { ...payload },
      });
      window.dispatchEvent(linked_goals_event);
      if (!this.link_goals_wealth_state.define_contribution_later) {
        await this.loadRecurrentContributionInformation(true);
      }
      return true;
    } catch {
      this.message_notifier.showErrorNotification(this.translate('errors.create_link_sowos_wealth'));
      return false;
    } finally {
      this.link_goals_wealth_state.is_loading = false;
    }
  }
}
